import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/ProductFeaturesService';
import { ProductFeature } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useProductFeature = (productFeatureId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const productFeatureQuery = useQuery({
        queryKey: ['product-feature', productFeatureId],
        queryFn: () => fetch(productFeatureId!),
        enabled: !!productFeatureId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: ProductFeature) => {
            const { created: _created, updated: _updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            notifySuccess('Product Feature created');
            queryClient.invalidateQueries({ queryKey: ['product-features'] });
        },
        onError: notifyError
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: ProductFeature) => {
            if (!productFeatureId) throw new Error('productFeatureId is required parameter');
            const { created: _created, updated: _updated, ...rest } = values;
            return update(productFeatureId, rest);
        },
        onSuccess: () => {
            notifySuccess('Product Feature updated');
            queryClient.invalidateQueries({ queryKey: ['product-features'] });
            queryClient.invalidateQueries({ queryKey: ['product-feature', productFeatureId] });
        },
        onError: notifyError
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!productFeatureId) throw new Error('productFeatureId is required parameter');
            return deleteRecord(productFeatureId);
        },
        onSuccess: () => {
            notifySuccess('Product Feature deleted');
            queryClient.invalidateQueries({ queryKey: ['product-features'] });
        },
        onError: notifyError
    });

    return {
        record: productFeatureQuery.data,
        loading: productFeatureQuery.isLoading,
        fetchRecord: productFeatureQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
